import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";

/**
 * FounderPitchPage — one-page print handout the founder can leave at
 * brokers, shippers, and TA truck-stop bulletin boards to explain the
 * DNP database in 30 seconds.
 *
 * Route: /founder-pitch
 *
 * Usage:
 *   1. Open /founder-pitch?name=Michael+Guley&phone=(702)+680-5540
 *   2. Ctrl+P → Save as PDF or print on Letter paper
 *   3. Slide under office doors, hand out at TA cafeterias, staple to
 *      dispatch cork-boards, drop 5 copies at every trucking-Facebook meetup.
 *
 * Query params (all optional):
 *   name  — founder's full name (default: Michael Guley)
 *   phone — founder's phone (default: (702) 680-5540)
 *   role  — founder's title (default: Founder, FreightPilotAI)
 */
export default function FounderPitchPage() {
  const [params] = useSearchParams();
  const [name, setName] = useState((params.get("name") || "Michael Guley").trim());
  const [phone, setPhone] = useState((params.get("phone") || "(702) 680-5540").trim());
  const [role, setRole] = useState((params.get("role") || "Founder, FreightPilotAI").trim());

  useEffect(() => {
    document.title = "FreightPilotAI — Dispatch, invoicing & payroll for small carriers";
  }, []);

  return (
    <div className="min-h-screen bg-neutral-100 text-black">
      <style>{`
        @page { size: Letter portrait; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; }
          .pitch-sheet { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      {/* Instruction bar — screen only */}
      <div className="no-print bg-neutral-900 text-white text-sm py-3 px-6 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <b>Ready to print.</b>{" "}
          Press <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Ctrl+P</kbd> or{" "}
          <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Cmd+P</kbd>. Save as PDF, or send to your printer to hand out at TA truck stops / trucking meetups.
        </div>
        <button onClick={() => window.print()}
          className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold rounded-md px-4 py-2 text-sm shrink-0"
          data-testid="pitch-print-btn">
          Print now
        </button>
      </div>

      {/* Screen-only editor */}
      <div className="no-print max-w-3xl mx-auto mt-6 mb-4 p-5 rounded-lg border-2 border-[#FF5A1F] bg-white shadow-lg">
        <div className="font-bold text-neutral-900 mb-1 text-base">
          Fill in your info — then press "Print now" (top-right).
        </div>
        <div className="text-xs text-neutral-600 mb-4">
          Nothing is saved. This page prints exactly what you see below.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">Your name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="pitch-input-name"
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">Your title</span>
            <input type="text" value={role} onChange={(e) => setRole(e.target.value)}
              data-testid="pitch-input-role"
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">Your phone</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              data-testid="pitch-input-phone"
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none" />
          </label>
        </div>
      </div>

      {/* Print sheet — Letter size */}
      <div className="pitch-sheet mx-auto bg-white shadow-lg" style={{ width: "8.5in", minHeight: "11in", padding: "0.65in 0.7in" }} data-testid="pitch-sheet">
        {/* Top banner */}
        <div className="flex items-center justify-between border-b-4 border-black pb-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">FREE tool for small carriers</div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-4xl font-black tracking-tight text-black">Freight</span>
              <span className="text-4xl font-black tracking-tight" style={{ color: "#FF5A1F" }}>Pilot</span>
              <span className="text-4xl font-black tracking-tight text-black">AI</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Built by carriers, for carriers</div>
            <div className="text-sm font-mono text-black mt-1">freightpilotai.ai</div>
          </div>
        </div>

        {/* Hook */}
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: "#FF5A1F" }}>
            The 30-second pitch
          </div>
          <h1 className="text-5xl font-black leading-[0.95] tracking-tight mb-4 text-black">
            Stop getting<br />
            <span style={{ color: "#FF5A1F" }}>scammed by brokers.</span>
          </h1>
          <p className="text-lg text-neutral-700 leading-snug max-w-2xl">
            A community-sourced Do-Not-Pay database for small carriers.
            <br />
            <b className="text-black">100% free. No sign-up required. No upsell.</b>
          </p>
        </div>

        {/* Three-column value prop */}
        <div className="grid grid-cols-3 gap-5 mb-8">
          <div className="border-l-4 border-black pl-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
              1 — Search
            </div>
            <div className="font-black text-black text-xl leading-tight mb-1">
              Look up any MC#
            </div>
            <div className="text-[13px] text-neutral-700 leading-snug">
              Type a broker's MC or DOT number at{" "}
              <b className="font-mono" style={{ color: "#FF5A1F" }}>freightpilotai.ai/dnp</b>{" "}
              and instantly see if other carriers reported them.
            </div>
          </div>
          <div className="border-l-4 pl-4" style={{ borderColor: "#FF5A1F" }}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
              2 — Extension
            </div>
            <div className="font-black text-black text-xl leading-tight mb-1">
              Auto-flag on DAT
            </div>
            <div className="text-[13px] text-neutral-700 leading-snug">
              Install <b>LoadFilter</b> for Chrome — it puts an on-screen flag on brokers from the community reputation database while you're browsing DAT, Truckstop, or TruckerPath.
            </div>
          </div>
          <div className="border-l-4 border-black pl-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
              3 — Report back
            </div>
            <div className="font-black text-black text-xl leading-tight mb-1">
              Warn other carriers
            </div>
            <div className="text-[13px] text-neutral-700 leading-snug">
              Got burned? Submit a moderated report in 60 seconds. Every submission makes the DAT filter smarter for the next carrier.
            </div>
          </div>
        </div>

        {/* Two-column body */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "#FF5A1F" }}>
              Also free
            </div>
            <ul className="space-y-2 text-[14px] text-black">
              <li className="flex items-start gap-2">
                <span className="mt-0.5" style={{ color: "#FF5A1F" }}>■</span>
                <span><b>BrokerCheck</b> — real-time FMCSA verification (authority, insurance, days-to-pay reports)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5" style={{ color: "#FF5A1F" }}>■</span>
                <span><b>RateCheck</b> — spot-check any lane rate against national market averages so you catch lowballs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5" style={{ color: "#FF5A1F" }}>■</span>
                <span><b>Careers page builder</b> — free hosted job page for your fleet, indexed on Google Jobs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5" style={{ color: "#FF5A1F" }}>■</span>
                <span><b>Docs Vault</b> — store COI, W-9, MC authority, IFTA in one place (free tier)</span>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "#FF5A1F" }}>
              Why this exists
            </div>
            <p className="text-[14px] text-neutral-800 leading-snug">
              I'm a solo founder running a small trucking company. I lost a full pay week to a broker who ghosted after delivery. Called RMIS, called Ansonia — both quoted $99+/month before they'd tell me anything.
            </p>
            <p className="text-[14px] text-neutral-800 leading-snug mt-2">
              So I built the database I wish existed: <b className="text-black">free, moderated, and running on the same trust the trucking Facebook groups already run on.</b> Every carrier who reports a broker makes it better for the next one.
            </p>
          </div>
        </div>

        {/* Big CTA box */}
        <div className="rounded-lg p-5 mb-6 flex items-center gap-6" style={{ background: "#000", color: "#fff" }}>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#FF5A1F" }}>Start here today</div>
            <div className="font-black text-2xl leading-tight">
              freightpilotai.ai / tools
            </div>
            <div className="text-[13px] mt-1 text-neutral-300">
              All 6 tools free. No account required for BrokerCheck, RateCheck, or DNP search.
            </div>
          </div>
          <div className="text-right">
            <div className="w-32 h-32 bg-white rounded p-1.5 flex items-center justify-center" data-testid="pitch-qr">
              <QRCodeCanvas
                value="https://freightpilotai.ai/tools?utm_source=founder_pitch_print"
                size={116}
                level="M"
                includeMargin={false}
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </div>
            <div className="text-[9px] uppercase tracking-widest font-bold mt-1.5" style={{ color: "#FF5A1F" }}>
              ← Scan for all 6 free tools
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-black pt-3 flex items-end justify-between text-[11px]">
          <div>
            <div className="font-bold text-black">{name}</div>
            <div className="text-neutral-600">{role}</div>
          </div>
          <div className="text-right text-neutral-700">
            <div className="font-mono font-bold text-black text-sm">{phone}</div>
            <div className="text-[10px]">contact@freightpilotai.ai</div>
          </div>
        </div>

        {/* Legal fine print */}
        <div className="mt-3 text-[9px] text-neutral-500 leading-snug">
          FreightPilotAI is an independent tool built by a working carrier. DNP reports are user-submitted opinions, each moderated before publication. Not a substitute for RMIS, Ansonia, or legal counsel. Contact us to dispute a report about your company.
        </div>
      </div>

      <div className="no-print h-10" />
    </div>
  );
}
